import { useIntl } from 'react-intl';
import moment from 'moment';
import { Checkbox, Col, DatePicker, Input, Row, Select } from 'antd';

import {
  ComplementOriginEnum,
  ComplementOriginTranslator,
  CustomerType,
  ListWorkOrdersQuery,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementStatusNumber,
  WorkOrderComplementTypeNumber,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';

import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import {
  CompaniesInfiniteSelectField,
  InputDate,
  InputText,
} from '@/components/_common';
import { DateTimeFormat } from '@/utils/moment';
import TagsInfiniteSelectField from '@/components/_common/TagsInfiniteSelectField';
import MaskedInput from 'react-text-mask';
import { licensePlateMask } from '@/utils/mask/commonMasks';
import StatusSelectField from '@/components/_common/StatusSelectField';
import { isAppPortocar } from '@/index';

function WorkOrdersExtraFilters({
  queryParams,
  setQueryParams,
}: ExtraFiltersProps<ListWorkOrdersQuery>) {
  const intl = useIntl();

  const originBlocked = isAppPortocar
    ? new Set([ComplementOriginEnum.MyBoschCarService])
    : new Set([
        ComplementOriginEnum.PortoBattery,
        ComplementOriginEnum.PortoSchedule,
      ]);

  const complementOriginArray = Object.values(ComplementOriginEnum)
    .filter((origin) => !originBlocked.has(origin))
    .map((value) => {
      const origins = Object.values(ComplementOriginEnum);

      return {
        value: origins.findIndex((origin) => origin === value),
        label: ComplementOriginTranslator[value],
      };
    });

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <CompaniesInfiniteSelectField
          onChange={(values) => {
            setQueryParams((prev) => ({ ...prev, companiesIds: values }));
          }}
          value={queryParams?.companiesIds}
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.company',
          })}
          allowClear
          mode="multiple"
          queryFilter={{ rows: 50 }}
          queryEnable={!!queryParams?.companiesIds?.length}
        />
      </Col>

      <Col span={24} md={12}>
        <InputText
          onlyNumber
          value={queryParams?.number}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({
              ...prev,
              number: value,
            }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.number',
          })}
        />
      </Col>

      <Col span={24} md={12}>
        <Input
          value={queryParams?.estimator}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, estimator: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.estimator',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={12} xxl={12}>
        <Input
          value={queryParams?.customer}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, customer: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.customer',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={12} xxl={12}>
        <Input
          value={queryParams?.insurer}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, insurer: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.insurer',
          })}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.customerType}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, customerType: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.customerType',
          })}
          options={Object.values(CustomerType).map((item) => ({
            label: intl.formatMessage({ id: `workOrders.${item}` }),
            value: item,
          }))}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Input
          value={queryParams?.vehicle}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, vehicle: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.vehicle',
          })}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Input
          value={queryParams?.fleet}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, fleet: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.vehicle.fleet',
          })}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={4}>
        <MaskedInput
          value={queryParams?.licensePlate}
          className="ant-input"
          mask={licensePlateMask}
          guide={false}
          name="licensePlate"
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({
              ...prev,
              licensePlate: value.toUpperCase(),
            }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.vehicle.licensePlate',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={4}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.estimatedLeaveDate',
          })}
          value={
            queryParams?.estimatedLeaveDate
              ? moment(queryParams?.estimatedLeaveDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              estimatedLeaveDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={4}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.entryDate',
          })}
          value={
            queryParams?.entryDate ? moment(queryParams?.entryDate) : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              entryDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={4}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.authorizationDate',
          })}
          value={
            queryParams?.authorizationDate
              ? moment(queryParams?.authorizationDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              authorizationDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={4}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.closingDate',
          })}
          value={
            queryParams?.closingDate
              ? moment(queryParams?.closingDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              closingDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={4} xxl={4}>
        <DatePicker
          format="DD/MM/YYYY"
          style={{ width: '100%' }}
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.returnDate',
          })}
          value={
            queryParams?.returnDate
              ? moment(queryParams?.returnDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              returnDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.status}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, status: value }))
          }
          placeholder="Situação"
          mode="multiple"
        >
          <Select.Option value={WorkOrderComplementStatusNumber.Open}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.open',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementStatusNumber.Authorized}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.authorized',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementStatusNumber.Closed}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.closed',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementStatusNumber.Cancelled}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.cancelled',
            })}
          </Select.Option>
        </Select>
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          mode="multiple"
          maxTagTextLength={30}
          maxTagCount="responsive"
          style={{ width: '100%' }}
          options={complementOriginArray}
          placeholder="Origem do orçamento"
          value={queryParams?.complementOrigin}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, complementOrigin: value }))
          }
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Input
          value={queryParams?.indicatedBy}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, indicatedBy: value }))
          }
          placeholder="Corretor"
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.type}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, type: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.type',
          })}
          mode="multiple"
        >
          <Select.Option value={WorkOrderComplementTypeNumber.Courtesy}>
            {intl.formatMessage({
              id: `workOrders.domain.enums.work_order_type.${
                isAppPortocar ? 'insideCourtesy' : 'courtesy'
              }`,
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.Warranty}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.warranty',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.WorkOrder}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.work_order',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.TotalLoss}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.total_loss',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.Return}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.return',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.Revision}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.revision',
            })}
          </Select.Option>
        </Select>
      </Col>

      <Col span={24} md={12} lg={8}>
        <TagsInfiniteSelectField
          onChange={(values) => {
            setQueryParams((prev) => ({ ...prev, tagsId: values }));
          }}
          value={queryParams?.tagsId}
          queryEnable={!!queryParams?.tagsId}
          placeholder="Etiquetas"
          allowClear
          mode="multiple"
        />
      </Col>

      <Col span={24} md={12} lg={8}>
        <StatusSelectField
          onChange={(values) => {
            setQueryParams((prev) => ({ ...prev, statusId: values }));
          }}
          value={queryParams?.statusId}
          queryEnable={!!queryParams?.statusId}
          placeholder="Status"
          allowClear
          mode="multiple"
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Input
          value={queryParams?.prisma}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, prisma: value }))
          }
          placeholder="Prisma"
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <OrderExtraFilter
          optionsValue={{ asc: 0, desc: 1 }}
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>

      {isAppPortocar && (
        <>
          <Col
            span={24}
            md={8}
            lg={8}
            xxl={8}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Checkbox
              checked={queryParams?.isPortoBatteryWorkOrder}
              onChange={({ target }) =>
                setQueryParams((prev) => ({
                  ...prev,
                  isPortoBatteryWorkOrder: target.checked,
                }))
              }
            >
              Filtrar bateria (7051)
            </Checkbox>
          </Col>

          <Col
            span={24}
            md={8}
            lg={8}
            xxl={8}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Checkbox
              checked={queryParams?.workOrderWithBatteryWarranty}
              onChange={({ target }) =>
                setQueryParams((prev) => ({
                  ...prev,
                  workOrderWithBatteryWarranty: target.checked,
                }))
              }
            >
              Filtrar com garantia de bateria
            </Checkbox>
          </Col>

          <Col
            span={24}
            md={8}
            lg={8}
            xxl={8}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <InputDate
              placeholder="Início do agendamento"
              disabledDate={(date) =>
                !!queryParams?.scheduleEndDate &&
                date.isAfter(moment(queryParams.scheduleEndDate))
              }
              value={
                queryParams?.scheduleStartDate
                  ? moment(queryParams?.scheduleStartDate)
                  : undefined
              }
              onChange={(value) =>
                setQueryParams((prev) => ({
                  ...prev,
                  scheduleStartDate: value?.format(DateTimeFormat),
                  scheduleEndDate: !value ? undefined : prev.scheduleEndDate,
                }))
              }
            />
          </Col>

          <Col
            span={24}
            md={8}
            lg={8}
            xxl={8}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <InputDate
              placeholder="Fim do agendamento"
              disabled={!queryParams?.scheduleStartDate}
              disabledDate={(date) =>
                !!queryParams?.scheduleStartDate &&
                date.isBefore(moment(queryParams.scheduleStartDate))
              }
              value={
                queryParams?.scheduleEndDate
                  ? moment(queryParams?.scheduleEndDate)
                  : undefined
              }
              onChange={(value) =>
                setQueryParams((prev) => ({
                  ...prev,
                  scheduleEndDate: value?.format(DateTimeFormat),
                }))
              }
            />
          </Col>
        </>
      )}
    </Row>
  );
}

export default WorkOrdersExtraFilters;
